/**
 * Graphique temporel partagé (voir `timeSeriesConfig.ts` pour les règles).
 * Offre une bascule accessible « voir en tableau » (m-10) : les mêmes valeurs,
 * sans dépendance à la couleur ni au pointeur.
 */
import React, { useMemo, useState } from 'react';
import { Button, Card, Space, Table, Typography } from 'antd';
import { LineChartOutlined, TableOutlined } from '@ant-design/icons';
import { Area, Line } from '@ant-design/charts';
import { useTranslation } from 'react-i18next';
import type { Series } from '../../../api/monitoring/v2';
import { INK, cardStyle } from '../monitoringTheme';
import { buildTableRows, buildTimeSeriesConfig, grainOf, type ChartDatum } from './timeSeriesConfig';
import { formatAxisDate, parseLocalDate, useFormat } from './format';
import { directionOf } from '../../../i18n/uiLocale';

const { Text } = Typography;

export type TimeSeriesChartProps = {
    title: string;
    subtitle?: string;
    series: Series[];
    kind?: 'line' | 'area';
    stacked?: boolean;
    /** Formatage d'une valeur (comptage, minutes, montant avec devise…). */
    formatValue: (raw: number | string, currency?: string) => string;
    height?: number;
};

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
    title,
    subtitle,
    series,
    kind = 'line',
    stacked,
    formatValue,
    height = 260,
}) => {
    const { t, i18n } = useTranslation();
    const f = useFormat();
    const [asTable, setAsTable] = useState(false);
    const direction = directionOf(i18n.language);

    const { config, legend } = useMemo(
        () =>
            buildTimeSeriesConfig({
                series,
                lang: f.lang,
                direction,
                translate: (key) => t(key),
                formatValue: (raw, d: ChartDatum) => formatValue(raw, d.currency),
                stacked,
            }),
        [series, f.lang, direction, t, formatValue, stacked]
    );

    const grain = grainOf(series);
    const rows = useMemo(() => buildTableRows(series), [series]);
    const Chart = kind === 'area' ? Area : Line;

    return (
        <Card
            style={cardStyle}
            title={
                <Space direction="vertical" size={0}>
                    <Text strong>{title}</Text>
                    {subtitle && <Text style={{ fontSize: 11, color: INK.muted, fontWeight: 400 }}>{subtitle}</Text>}
                </Space>
            }
            extra={
                <Button
                    size="small"
                    type="text"
                    aria-pressed={asTable}
                    icon={asTable ? <LineChartOutlined /> : <TableOutlined />}
                    onClick={() => setAsTable((v) => !v)}
                >
                    {asTable ? t('monitoring.chart.showChart') : t('monitoring.chart.showTable')}
                </Button>
            }
        >
            {asTable ? (
                <Table
                    size="small"
                    rowKey="t"
                    pagination={false}
                    scroll={{ x: 'max-content', y: height }}
                    dataSource={rows}
                    columns={[
                        {
                            key: 't',
                            dataIndex: 't',
                            title: t('monitoring.chart.period'),
                            render: (value: string) => formatAxisDate(f.lang, parseLocalDate(value), grain === 'month' ? 'month' : 'day'),
                        },
                        ...legend.map((entry, idx) => ({
                            key: entry.key,
                            dataIndex: entry.key,
                            title: entry.label,
                            align: 'end' as const,
                            render: (value: number | string | undefined) =>
                                value === undefined ? '—' : formatValue(value, series[idx]?.currency),
                        })),
                    ]}
                />
            ) : (
                // Le canvas reste en LTR : c'est l'échelle (range inversé) qui
                // porte le sens de lecture, pas le flux du texte.
                <div dir="ltr" role="img" aria-label={title}>
                    <Chart {...(config as any)} height={height} />
                </div>
            )}
        </Card>
    );
};

export default TimeSeriesChart;
